import type {
    CodeflingDetailResponse,
    CodeflingFeaturedResponse,
    CodeflingFiltersResponse,
    CodeflingFindResponse,
    CodeflingInstallPayload,
    CodeflingInstallPlanResponse,
    CodeflingPackageSearchParams,
    CodeflingPartnersResponse,
} from '~/api/models/CodeflingPackage';
import type { InstalledItem } from '~/types/plugin-browser';
import RequestService from './request';

const buildSearchPayload = (params: CodeflingPackageSearchParams): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};

    if (params.q) {
        payload.q = params.q;
    }
    if (params.category != null) {
        payload.category = params.category;
    }
    if (params.tags && params.tags.length > 0) {
        payload.tags = params.tags;
    }
    if (params.paid) {
        payload.paid = params.paid;
    }
    if (params.sort) {
        payload.sort = params.sort;
    }
    if (params.sortdir) {
        payload.sortdir = params.sortdir;
    }
    if (typeof params.page === 'number') {
        payload.page = params.page;
    }
    if (typeof params.perPage === 'number') {
        payload.per_page = params.perPage;
    }

    return payload;
};

class CodeflingPackagesService {
    find(params: CodeflingPackageSearchParams): Promise<CodeflingFindResponse> {
        return RequestService.get('/servers/:server/codefling/packages/find', buildSearchPayload(params));
    }

    show(id: number): Promise<CodeflingDetailResponse> {
        return RequestService.get(`/servers/:server/codefling/packages/${id}`);
    }

    filters(): Promise<CodeflingFiltersResponse> {
        return RequestService.get('/servers/:server/codefling/packages/filters');
    }

    featured(): Promise<CodeflingFeaturedResponse> {
        return RequestService.get('/servers/:server/codefling/packages/featured');
    }

    partners(): Promise<CodeflingPartnersResponse> {
        return RequestService.get('/servers/:server/codefling/packages/partners');
    }

    // Free plugins reuse the existing Rust plugin install pipeline (game_mods).
    install(payload: CodeflingInstallPayload): Promise<unknown> {
        return RequestService.post('/servers/:server/rust/plugins', payload);
    }

    // What the install would write, so it can be confirmed before anything lands.
    preview(payload: CodeflingInstallPayload): Promise<CodeflingInstallPlanResponse> {
        return RequestService.post('/servers/:server/rust/plugins/preview', payload);
    }

    // Installs land in game_mods regardless of which browser started them, so the
    // same endpoint the Plugin Installer reads tells us what is already on the server.
    installed(): Promise<{ data: { attributes: InstalledItem }[] }> {
        return RequestService.get('/servers/:server/rust/plugins');
    }
}

export default new CodeflingPackagesService();
